import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { Clock, ArrowLeft, Tag, Phone, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import InquiryForm from '../components/InquiryForm';
import { getBlogPost } from '../services/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const data = await getBlogPost(slug);
        setPost(data);
        setError(null);
      } catch (err) {
        setError('Article not found');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
    window.scrollTo(0, 0);
  }, [slug]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Helmet>
          <title>Loading... | Close Now Lending Blog</title>
        </Helmet>
        <Header />
        <div className="flex justify-center items-center min-h-[60vh]">
          <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen">
        <Helmet>
          <title>Article Not Found | Close Now Lending Blog</title>
        </Helmet>
        <Header />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-blue-600 hover:underline">Back to Learning Center</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{String(post.title || 'Blog Post') + ' | Close Now Lending Blog'}</title>
        <meta name="description" content={String(post.excerpt || '')} />
        <meta name="keywords" content={String((post.tags || []).join(', '))} />
        <link rel="canonical" href={'https://closenowlending.com/blog/' + String(post.slug || '')} />

        {/* Open Graph */}
        <meta property="og:title" content={String(post.title || 'Blog Post')} />
        <meta property="og:description" content={String(post.excerpt || '')} />
        <meta property="og:url" content={'https://closenowlending.com/blog/' + String(post.slug || '')} />
        <meta property="og:type" content="article" />
        {post.coverImage && <meta property="og:image" content={post.coverImage} />}
        <meta property="article:published_time" content={String(post.publishedAt || '')} />
        <meta property="article:author" content={String(post.author || 'Close Now Lending')} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={String(post.title || 'Blog Post')} />
        <meta name="twitter:description" content={String(post.excerpt || '')} />
        {post.coverImage && <meta name="twitter:image" content={post.coverImage} />}

        {/* Article Schema */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.excerpt,
          "image": post.coverImage || '',
          "author": { "@type": "Organization", "name": post.author || "Close Now Lending" },
          "publisher": { "@type": "Organization", "name": "Close Now Lending Corp" },
          "datePublished": post.publishedAt,
          "url": 'https://closenowlending.com/blog/' + (post.slug || ''),
          "keywords": (post.tags || []).join(', ')
        })}</script>
      </Helmet>
      <Header />

      {/* Hero */}
      <section className="relative pt-20" data-testid="blog-post-hero">
        {post.coverImage && (
          <>
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${post.coverImage})` }} />
            <div className="absolute inset-0 bg-slate-900/80" />
          </>
        )}
        {!post.coverImage && <div className="absolute inset-0 bg-slate-900" />}
        <div className="relative z-10 container mx-auto px-4 pt-12 pb-16">
          <Link to="/blog" className="inline-flex items-center text-gray-300 hover:text-white text-sm mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Learning Center
          </Link>
          <div className="max-w-3xl">
            <span className="inline-block bg-blue-600 text-white text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded mb-4">
              {post.category}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-gray-300 text-sm">
              <span>{post.author}</span>
              <span className="w-1 h-1 bg-gray-500 rounded-full" />
              <span>{formatDate(post.publishedAt)}</span>
              <span className="w-1 h-1 bg-gray-500 rounded-full" />
              <span className="flex items-center"><Clock className="h-3.5 w-3.5 mr-1" />{post.readTime} min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="bg-white">
        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 max-w-7xl mx-auto">

            {/* Sidebar */}
            <aside className="lg:col-span-1 order-2 lg:order-1">
              <div className="lg:sticky lg:top-24 space-y-6">
                {/* Tags */}
                {post.tags.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-3">Topics</h3>
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag) => (
                        <span key={tag} className="flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">
                          <Tag className="h-3 w-3" />{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* CTA */}
                <div className="bg-slate-900 rounded-xl p-5">
                  <h4 className="text-white font-bold mb-2">Need Financing?</h4>
                  <p className="text-gray-300 text-sm mb-4">Let's discuss the right loan product for your investment.</p>
                  <Button
                    onClick={() => setIsFormOpen(true)}
                    data-testid="blog-post-apply-btn"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Apply Now
                  </Button>
                  <a href="tel:903-476-2937" className="flex items-center justify-center gap-2 text-gray-300 hover:text-white text-sm mt-3 transition-colors">
                    <Phone className="h-4 w-4" />903-476-2937
                  </a>
                </div>

                {/* Related links */}
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-3">Resources</h3>
                  <div className="space-y-2">
                    <Link to="/dscr-calculator" className="block text-sm text-blue-600 hover:text-blue-800 transition-colors">DSCR Calculator</Link>
                    <Link to="/deal-calculator" className="block text-sm text-blue-600 hover:text-blue-800 transition-colors">Fix & Flip Calculator</Link>
                    <Link to="/blog" className="block text-sm text-blue-600 hover:text-blue-800 transition-colors">All Articles</Link>
                  </div>
                </div>
              </div>
            </aside>

            {/* Article Body */}
            <main className="lg:col-span-3 order-1 lg:order-2" data-testid="blog-post-content">
              <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-blue-600 prose-strong:text-gray-900 prose-li:text-gray-700">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h2: ({ children }) => <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-xl font-bold text-gray-900 mt-8 mb-3">{children}</h3>,
                    p: ({ children }) => <p className="text-gray-700 leading-relaxed mb-4">{children}</p>,
                    ul: ({ children }) => <ul className="space-y-2 mb-6 list-none pl-0">{children}</ul>,
                    ol: ({ children }) => <ol className="space-y-2 mb-6 list-decimal pl-6">{children}</ol>,
                    li: ({ children }) => (
                      <li className="flex items-start gap-2 text-gray-700">
                        <span className="mt-2 w-1.5 h-1.5 bg-blue-600 rounded-full flex-shrink-0" />
                        <span>{children}</span>
                      </li>
                    ),
                    strong: ({ children }) => <strong className="font-bold text-gray-900">{children}</strong>,
                    a: ({ href, children }) => (
                      <Link to={href || '#'} className="text-blue-600 hover:text-blue-800 underline font-medium">
                        {children}
                      </Link>
                    ),
                    table: ({ children }) => (
                      <div className="overflow-x-auto mb-6 rounded-lg border border-gray-200">
                        <table className="w-full text-sm">{children}</table>
                      </div>
                    ),
                    thead: ({ children }) => <thead className="bg-slate-900 text-white">{children}</thead>,
                    th: ({ children }) => <th className="px-4 py-3 text-left font-semibold text-sm">{children}</th>,
                    td: ({ children }) => <td className="px-4 py-3 border-t border-gray-100 text-gray-700">{children}</td>,
                    tr: ({ children }) => <tr className="even:bg-gray-50">{children}</tr>,
                  }}
                >
                  {post.content}
                </ReactMarkdown>
              </div>

              {/* Bottom CTA */}
              <div className="mt-16 bg-slate-900 rounded-2xl p-8 md:p-12">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
                <p className="text-gray-300 mb-8 max-w-2xl">
                  Contact Close Now Lending today to discuss financing options for your next real estate investment.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button onClick={() => setIsFormOpen(true)} size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8">
                    <span>Apply Now</span>
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                  <a href="tel:903-476-2937">
                    <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8">
                      <Phone className="h-5 w-5 mr-2" />Call 903-476-2937
                    </Button>
                  </a>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      <Footer />
      <InquiryForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} loanType="" />
    </div>
  );
};

export default BlogPostPage;
